// Owner-stamped localStorage offline queue for the Requests capture button
// (franklycatering #497). See scratch/plan.md §5 for the full design
// writeup, incl. revision 4 / T3 (401 retryable, token read fresh at send
// time — handled in useRequestQueue.ts, not here).

export interface RequestPayload {
  id: string;
  message: string;
  page_path?: string | null;
  page_url?: string | null;
  page_name?: string | null;
  element?: unknown;
  viewport?: unknown;
  device_type?: string | null;
  dialog_title?: string | null;
  user_agent?: string | null;
  captured_at?: string;
  screenshot_b64?: string;
}

export interface QueueEnvelope {
  owner_id: string | null;
  queued_at: string;
  payload: RequestPayload;
}

const STORAGE_KEY = "tc_request_queue";
const MAX_QUEUED = 10;
const MAX_SCREENSHOT_CHARS = 1_500_000;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isEnvelope(v: unknown): v is QueueEnvelope {
  if (!v || typeof v !== "object") return false;
  const e = v as Record<string, unknown>;
  return (
    (typeof e.owner_id === "string" || e.owner_id === null) &&
    typeof e.queued_at === "string" &&
    !!e.payload &&
    typeof e.payload === "object"
  );
}

export function read(): QueueEnvelope[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((e): e is QueueEnvelope => {
      if (!isEnvelope(e)) return false;
      const age = now - Date.parse(e.queued_at);
      return Number.isFinite(age) && age <= MAX_AGE_MS;
    });
  } catch {
    return [];
  }
}

function stripScreenshot(payload: RequestPayload): RequestPayload {
  if (!payload.screenshot_b64) return payload;
  const { screenshot_b64: _drop, ...rest } = payload;
  return rest;
}

function write(items: QueueEnvelope[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

/** Text beats pictures — never lose the words to store the image. */
export function enqueue(payload: RequestPayload, ownerId: string | null): boolean {
  const items = read();
  const mine = items.filter((e) => e.owner_id === ownerId).slice(-(MAX_QUEUED - 1));
  const foreign = items.filter((e) => e.owner_id !== ownerId);

  const trimmedPayload = { ...payload };
  if (trimmedPayload.screenshot_b64 && trimmedPayload.screenshot_b64.length > MAX_SCREENSHOT_CHARS) {
    delete trimmedPayload.screenshot_b64;
  }

  const next = [...foreign, ...mine, { owner_id: ownerId, queued_at: new Date().toISOString(), payload: trimmedPayload }];
  if (write(next)) return true;
  // Quota exceeded — retry text-only across the whole store.
  return write(next.map((e) => ({ ...e, payload: stripScreenshot(e.payload) })));
}

/** Only this owner's envelopes; excludes foreign/corrupt (null-owner) entries. */
export function pendingFor(ownerId: string | null): number {
  if (ownerId === null) return 0;
  return read().filter((e) => e.owner_id === ownerId).length;
}

function dequeue(id: string): void {
  const items = read().filter((e) => e.payload.id !== id);
  write(items);
}

export interface SubmitErrorLike {
  status?: number;
  message?: string;
}

/**
 * `status === 401` is retryable here (revision 4 / T3): a stale/expired JWT
 * resolves itself once gotrue refreshes, which distinguishes it from every
 * other 4xx (those "would fail identically forever" and are correctly
 * permanent). Without this, a background flush that hit a stale-token 401
 * would dequeue-and-discard the owner's own text with no toast — exactly the
 * silent data loss this queue exists to prevent.
 */
export function isRetryable(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;

  // A deadline abort (submit.ts's 8s/12s AbortController) rejects with a
  // DOMException named "AbortError" whose `.code` is the NUMBER 20
  // (DOMException.ABORT_ERR) and whose `.message` never matches the regex
  // below ("The operation was aborted.", etc.) — so without this explicit
  // check a slow connection that legitimately needs more time gets treated
  // as a PERMANENT failure and the flush loop silently discards the user's
  // held text. Same failure shape the 401 fix above closes, different
  // trigger: a request that is still in flight is not a rejection, and
  // holding it for the next retry is exactly what "nothing is ever
  // discarded without the user being told" requires.
  if (err instanceof DOMException && err.name === "AbortError") return true;

  const e = err as SubmitErrorLike | undefined;
  if (typeof e?.status === "number") {
    if (e.status === 429) return true;
    if (e.status === 401) return true;
    if (e.status >= 400 && e.status < 500) return false;
    if (e.status >= 500) return true; // via withErrorHandler — retryable
  }

  const withCode = err as { code?: unknown } | undefined;
  if (typeof withCode?.code === "string") return false; // Postgres-style error code — never held

  const message = (e?.message ?? String(err ?? "")).toLowerCase();
  return /failed to fetch|networkerror|network request failed|load failed|offline|timed out|timeout/.test(message);
}

/**
 * Sends every envelope belonging to `currentOwnerId`. Foreign envelopes
 * (including any corrupted `null`-stamped ones — which can only happen if
 * the store was hand-edited, since the caller only ever mounts for a
 * signed-in super_admin) are held: skipped, never sent, never dequeued.
 *
 * `send` is injected so this stays pure/testable; the real implementation
 * (useRequestQueue.ts) reads the access token fresh immediately before each
 * call, which is what makes treating 401 as retryable actually self-heal.
 */
export async function flush(
  currentOwnerId: string | null,
  send: (payload: RequestPayload) => Promise<void>,
): Promise<void> {
  if (currentOwnerId === null) return;
  const items = read();
  for (const envelope of items) {
    if (envelope.owner_id !== currentOwnerId) continue; // foreign — held, not sent, not dequeued
    try {
      await send(envelope.payload);
      dequeue(envelope.payload.id);
    } catch (err) {
      if (isRetryable(err)) {
        return; // keep the rest for next time
      }
      dequeue(envelope.payload.id); // permanent failure — drop, don't retry forever
    }
  }
}
